import http from "./httpService.ts";
import {AxiosError} from "axios";
import {
  QC_PROBLEM_TAGS,
  QCProblemTag,
} from "../models/constants/qcProblemTags.ts";
import {
  IQCNote,
  IQCNoteCreateRequest,
  IQCNoteUpdateRequest,
} from "../models/interfaces/qcNote.interface.ts";

/**
 * Typed error raised when a QC-note response does not match the frozen
 * backend contract (Todos 3-5). Consumers narrow on `err instanceof
 * QCNoteMalformedError` to distinguish contract drift from ordinary Axios
 * transport failures (`AxiosError`, propagated unchanged for 404 / 422 /
 * 500).
 */
export class QCNoteMalformedError extends Error {
  readonly kind = "malformed" as const;
  readonly detail: string;

  constructor(message: string, detail: string) {
    super(`${message}: ${detail}`);
    this.name = "QCNoteMalformedError";
    this.detail = detail;
  }
}

const isPlainObject = (
  value: unknown,
): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const LEGACY_TAG_ONLY_TEXT = "\u200B";

const isLegacyBlankTextValidation = (error: unknown): boolean => {
  if (!(error instanceof AxiosError) || error.response?.status !== 422 || !isPlainObject(error.response.data)) {
    return false;
  }
  const detail = error.response.data.detail;
  return Array.isArray(detail) && detail.some((entry) => {
    if (!isPlainObject(entry) || entry.msg !== "Value error, text must be nonblank") {
      return false;
    }
    const location = entry.loc;
    return Array.isArray(location) && location.length === 2 && location[0] === "body" && location[1] === "text";
  });
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const isQCProblemTag = (value: unknown): value is QCProblemTag => {
  if (typeof value !== "string") {
    return false;
  }
  const tags: readonly string[] = QC_PROBLEM_TAGS;
  return tags.includes(value);
};

const requireString = (
  record: Record<string, unknown>,
  field: string,
): string => {
  const raw = record[field];
  if (typeof raw !== "string") {
    throw new QCNoteMalformedError(
      "QC note field missing or wrong type",
      `${field}=${JSON.stringify(raw)}`,
    );
  }
  return raw;
};

const requireFiniteNumber = (
  record: Record<string, unknown>,
  field: string,
): number => {
  const raw = record[field];
  if (!isFiniteNumber(raw)) {
    throw new QCNoteMalformedError(
      "QC note field missing or wrong type",
      `${field}=${JSON.stringify(raw)}`,
    );
  }
  return raw;
};

const requireProblemTagOrNull = (
  record: Record<string, unknown>,
): QCProblemTag | null => {
  const raw = record.problemTag;
  if (raw === null) {
    return null;
  }
  if (isQCProblemTag(raw)) {
    return raw;
  }
  throw new QCNoteMalformedError(
    "QC note problemTag is neither null nor a known QCProblemTag",
    `problemTag=${JSON.stringify(raw)}`,
  );
};

/**
 * Parse ONE untrusted JSON object into a fully-typed `IQCNote`. Every field
 * of the frozen backend contract is checked; the parsed object is a fresh
 * value so extra wire fields never leak into the frontend model.
 */
const parseQCNote = (raw: unknown): IQCNote => {
  if (!isPlainObject(raw)) {
    throw new QCNoteMalformedError(
      "QC note payload is not an object",
      typeof raw,
    );
  }
  const text = requireString(raw, "text");
  const problemTag = requireProblemTagOrNull(raw);
  return {
    id: requireString(raw, "id"),
    projectId: requireString(raw, "projectId"),
    languageCode: requireString(raw, "languageCode"),
    timecodeSeconds: requireFiniteNumber(raw, "timecodeSeconds"),
    text: problemTag !== null && text === LEGACY_TAG_ONLY_TEXT ? "" : text,
    problemTag,
    createdBy: requireString(raw, "createdBy"),
    createdAt: requireString(raw, "createdAt"),
  };
};

const parseQCNoteList = (raw: unknown): IQCNote[] => {
  if (!Array.isArray(raw)) {
    throw new QCNoteMalformedError(
      "QC note list payload is not an array",
      typeof raw,
    );
  }
  return raw.map(parseQCNote);
};

/**
 * `GET /api/v1/projects/lite/{projectId}/qc-notes`.
 * Passing `language` appends `?language={lang}` to filter by target track;
 * omitting it returns notes across every language. The service returns the
 * FLAT list from the backend — grouping / sorting happens in the context
 * reducer (Todo 11), never here.
 */
export const getQCNotes = async (
  projectId: string,
  language?: string,
): Promise<IQCNote[]> => {
  const suffix =
    language === undefined ? "" : `?language=${encodeURIComponent(language)}`;
  const response = await http.get(
    `/api/v1/projects/lite/${projectId}/qc-notes${suffix}`,
  );
  return parseQCNoteList(response.data);
};

/**
 * `POST /api/v1/projects/lite/{projectId}/qc-notes` with a camelCase JSON
 * body matching the frozen contract. `problemTag` is only sent when the
 * caller provides one, matching the optional-key semantics of
 * `IQCNoteCreateRequest`. Backend replies with 201 + the persisted note.
 */
export const createQCNote = async (
  projectId: string,
  body: IQCNoteCreateRequest,
): Promise<IQCNote> => {
  const payload: Record<string, unknown> = {
    languageCode: body.languageCode,
    timecodeSeconds: body.timecodeSeconds,
  };
  if (body.problemTag !== undefined) {
    payload.problemTag = body.problemTag;
  }
  const url = `/api/v1/projects/lite/${projectId}/qc-notes`;
  const post = (text: string) => http.post(url, {...payload, text});
  try {
    const response = await post(body.text);
    return parseQCNote(response.data);
  } catch (error: unknown) {
    if (body.text !== "" || body.problemTag === undefined || !isLegacyBlankTextValidation(error)) {
      throw error;
    }
    const response = await post(LEGACY_TAG_ONLY_TEXT);
    return parseQCNote(response.data);
  }
};

/**
 * `PUT /api/v1/projects/lite/{projectId}/qc-notes/{noteId}` — replaces the
 * two editable fields of an already-saved note. `problemTag` is always sent,
 * including as `null`, because that is how a tag is cleared. Backend replies
 * with 200 + the updated note.
 */
export const updateQCNote = async (
  projectId: string,
  noteId: string,
  body: IQCNoteUpdateRequest,
): Promise<IQCNote> => {
  const url = `/api/v1/projects/lite/${projectId}/qc-notes/${noteId}`;
  const put = (text: string) => http.put(url, {text, problemTag: body.problemTag});
  try {
    const response = await put(body.text);
    return parseQCNote(response.data);
  } catch (error: unknown) {
    if (body.text !== "" || body.problemTag === null || !isLegacyBlankTextValidation(error)) {
      throw error;
    }
    const response = await put(LEGACY_TAG_ONLY_TEXT);
    return parseQCNote(response.data);
  }
};

/**
 * `DELETE /api/v1/projects/lite/{projectId}/qc-notes/{noteId}` — expects
 * 204 no-content on success. Axios rejects any non-2xx as `AxiosError`,
 * which propagates unchanged.
 */
export const deleteQCNote = async (
  projectId: string,
  noteId: string,
): Promise<void> => {
  await http.delete(
    `/api/v1/projects/lite/${projectId}/qc-notes/${noteId}`,
  );
};
